import React, { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { ThemeProvider } from './components/theme/ThemeProvider'
import { Layout } from './components/layout/Layout'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { TermsGuard } from './components/legal/TermsGuard'
import { Login } from './pages/Login/Login'
import { Register } from './pages/Register/Register'
import { ResetPassword } from './pages/ResetPassword/ResetPassword'
import { SetNewPassword } from './pages/ResetPassword/SetNewPassword'
import { Dashboard } from './pages/Dashboard/Dashboard'
import { ReferralDashboard } from './pages/Dashboard/ReferralDashboard'
import { IxenConsole } from './pages/Trading/IxenConsole'
import MyCycles from './pages/Cycles/MyCycles'
import { IxenSystem } from './pages/Trading/IxenSystem'
import { EaDownloads } from './pages/Trading/EaDownloads'
import { Admin } from './pages/Admin/Admin'
import { Simulator } from './pages/Simulator/Simulator'
import { Calendar } from './pages/Calendar/Calendar'
import { IxenDashboard } from './pages/Ixen/IxenDashboard'
import { IxenAdmin } from './pages/Ixen/IxenAdmin'
import { BacktestDashboard } from './pages/Admin/BacktestDashboard'
import { IxenCycles } from './pages/Admin/IxenCycles'
import { ZanshinMonitor } from './pages/Admin/ZanshinMonitor'
import { ZanshinDemo } from './pages/Admin/ZanshinDemo'
import { SignalMonitor } from './pages/Admin/SignalMonitor'
import { Pricing } from './pages/Pricing/Pricing'
import { Notifications } from './pages/Notifications/Notifications'
import i18n from './i18n/config'
import { useAuthStore } from './stores/auth.store'

function AdminOnlyRoute({ children }: { children: React.ReactNode }) {
  const isAdmin = useAuthStore(state => state.isAdmin)
  return isAdmin ? <>{children}</> : <Navigate to="/trading/console" replace />
}

function IxenSystemRoute({ children }: { children: React.ReactNode }) {
  const hasIxenSystemAccess = useAuthStore(state => state.hasIxenSystemAccess)
  return hasIxenSystemAccess ? <>{children}</> : <Navigate to="/trading/console" replace />
}

function App() {
  const [refreshKey, setRefreshKey] = useState(0)
  const { token, updateUser } = useAuthStore()

  useEffect(() => {
    const handleLanguageChanged = () => {
      setRefreshKey(prev => prev + 1)
    }
    i18n.on('languageChanged', handleLanguageChanged)
    return () => {
      i18n.off('languageChanged', handleLanguageChanged)
    }
  }, [])

  useEffect(() => {
    if (!token) return
    fetch('/api/v1/auth/me', {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) updateUser(data) })
      .catch(() => {})
  }, [token])
  
  const isAuthenticated = useAuthStore((state) => state.checkAuth())

  return (
    <ThemeProvider defaultTheme="dark" storageKey="tradebridgepro-theme">
      <div key={refreshKey}>
        <BrowserRouter>
          <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/set-new-password" element={<SetNewPassword />} />
          
          {/* Root route - redirect based on auth status */}
          <Route 
            path="/" 
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          
          {/* Protected routes (inside Layout) */}
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="referral" element={<ReferralDashboard />} />
            
            <Route path="trading" element={<TermsGuard><Outlet /></TermsGuard>}>
              <Route index element={<Navigate to="/trading/console" replace />} />
              <Route path="manual" element={<Navigate to="/trading/console" replace />} />
              <Route path="automatic" element={<Navigate to="/trading/console" replace />} />
              <Route path="calculator" element={<Navigate to="/trading/console" replace />} />
              <Route path="console" element={<IxenConsole />} />
              <Route path="system" element={<IxenSystemRoute><IxenSystem /></IxenSystemRoute>} />
              <Route path="downloads" element={<IxenSystemRoute><EaDownloads /></IxenSystemRoute>} />
            </Route>

            <Route path="my-cycles" element={<AdminOnlyRoute><MyCycles /></AdminOnlyRoute>} />

            <Route path="notifications" element={<Notifications />} />
            
            <Route path="simulator" element={<Simulator />} />
            
            <Route path="pricing" element={<Pricing />} />
            
            <Route path="ixen" element={<IxenDashboard />} />
            
            <Route
              path="admin"
              element={
                <AdminOnlyRoute>
                  <Outlet />
                </AdminOnlyRoute>
              }
            >
              <Route path="ixen" element={<IxenAdmin />} />
              <Route path="monitor" element={<SignalMonitor />} />
              <Route index element={<Navigate to="/admin/users" replace />} />
              <Route path="users" element={<Admin />} />
              <Route path="subscriptions" element={<Admin />} />
              <Route path="propfirms" element={<Admin />} />
              <Route path="profits" element={<Admin />} />
              <Route path="emergency" element={<Admin />} />
              <Route path="analytics" element={<Admin />} />
              <Route path="terms" element={<Admin />} />
              <Route path="yomilog" element={<Admin />} />
              <Route path="sniperlog" element={<Admin />} />
              <Route path="alerts" element={<Admin />} />
              <Route path="auroux" element={<Admin />} />
              <Route path="zanshin" element={<ZanshinMonitor />} />
              <Route path="zanshin-demo" element={<ZanshinDemo />} />
              <Route path="backtest" element={<BacktestDashboard />} />
              <Route path="cycles" element={<IxenCycles />} />
            </Route>            
            <Route path="calendar">
              <Route index element={<Navigate to="/calendar/events" replace />} />
              <Route path="events" element={<Calendar />} />
              <Route path="import" element={<Calendar />} />
              <Route path="analytics" element={<Calendar />} />
            </Route>
          </Route>
          
          {/* Fallback route */}
          <Route 
            path="*" 
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
        </Routes>
      </BrowserRouter>
      </div>
    </ThemeProvider>
  )
}

export default App
